import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { creditCheckApi, type CreditAlarm, type CreditCheck, type CreditDispute } from "../api/creditCheck";
import { useAuth } from "../auth/AuthContext";
import { formatDate, formatMoney, formatNumber, intlLocale } from "../lib/format";
import Ic from "./icons.jsx";

/**
 * Bonitet kupca, uz njegovo ime.
 *
 * Stoji u zaglavlju stranice firme, a ne niže među karticama, jer menja odluku pre nego što se
 * bilo šta ugovori: firma u stečaju ili sa računom u blokadi neće platiti bez obzira na to kako
 * je saradnja izgledala do juče. Zato je prvo što se vidi status, pa ocena, pa brojevi.
 *
 * Podatak je snimak iz registra, ne poziv u trenutku gledanja, pa kartica uvek piše koliko je
 * star i nudi ponovno preuzimanje. „Nema provere" nije prazno polje nego rečenica sa razlogom:
 * firma van Srbije, bez PIB-a, ili registar koji nije odgovorio — to su tri različite stvari.
 */
export function CreditCheckCard({
  companyId, compact = false,
}: {
  companyId: string;
  /**
   * Zbijeno, za red pokazatelja na stranici finansija.
   *
   * Tamo stoji uz „Saldo" i „Dospelo", pa mora da ima njihovu visinu i njihov ritam: oznaka
   * gore, broj ispod, ostalo sitno. Ista komponenta, jer se podaci i pravila ne smeju
   * razilaziti između dva ekrana.
   */
  compact?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const locale = intlLocale(i18n.resolvedLanguage);
  const { hasPermission } = useAuth();
  const [podaci, setPodaci] = useState<CreditCheck | null>(null);
  const [ucitava, setUcitava] = useState(true);
  const [osvezava, setOsvezava] = useState(false);

  const sme = hasPermission("creditCheck:view");

  const ucitaj = useCallback(() => {
    if (!sme) return;
    setUcitava(true);
    creditCheckApi.get(companyId)
      .then(setPodaci)
      .catch(() => setPodaci(null))
      .finally(() => setUcitava(false));
  }, [companyId, sme]);

  useEffect(ucitaj, [ucitaj]);

  async function osvezi() {
    setOsvezava(true);
    try {
      setPodaci(await creditCheckApi.refresh(companyId));
    } catch {
      /* poruka ostaje na kartici iz prethodnog stanja */
    } finally {
      setOsvezava(false);
    }
  }

  if (!sme) return null;
  // Portal bez licence nema šta da kaže; prazna kartica bi bila samo šum u zaglavlju.
  if (!ucitava && (!podaci || podaci.reason === "NOT_CONFIGURED")) return null;

  if (compact) {
    return (
      <section style={zbijeno} aria-label={t("creditCheck.title", "Bonitet")}>
        <span style={naslov}>{t("creditCheck.title", "Bonitet")}</span>
        {ucitava ? (
          <span style={tiho}>{t("common.loading", "Učitavanje…")}</span>
        ) : !podaci?.available ? (
          <span style={tiho}>{razlog(podaci, t)}</span>
        ) : (
          <>
            <span style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <span style={{ ...ocenaBroj, color: bojaOcene(podaci.creditScore) }}>
                {podaci.creditScore ?? "—"}
              </span>
              {podaci.industryAvgScore != null && (
                <span style={tiho}>
                  {t("creditCheck.industryAvg", "prosek grane {{n}}",
                     { n: formatNumber(podaci.industryAvgScore, locale, { maximumFractionDigits: 0 }) })}
                </span>
              )}
            </span>
            <span style={{ ...znakoviOkvir, paddingTop: 0 }}>
              {podaci.alarms.length === 0 ? (
                <Znak ton="ok" tekst={t("creditCheck.clean", "Bez upozorenja")} />
              ) : (
                podaci.alarms.map((a) => (
                  <Znak
                    key={a.code}
                    ton={a.severity === "CRITICAL" ? "err" : "warn"}
                    tekst={opisAlarma(a, t)}
                    detalji={detaljiZnaka(a, podaci, locale, t)}
                  />
                ))
              )}
            </span>
          </>
        )}
      </section>
    );
  }

  return (
    <section style={okvir} aria-label={t("creditCheck.title", "Bonitet")}>
      <header style={zaglavlje}>
        <span style={naslov}>{t("creditCheck.title", "Bonitet")}</span>
        <span style={desno}>
          {podaci?.fetchedAt && (
            <span style={tiho}>
              {t("creditCheck.checkedAt", "Provereno {{datum}}", { datum: formatDate(podaci.fetchedAt) })}
            </span>
          )}
          <button
            type="button"
            className="btn ghost sm"
            style={dugmeOsvezi}
            disabled={osvezava}
            aria-label={t("creditCheck.refresh", "Proveri ponovo")}
            title={t("creditCheck.refresh", "Proveri ponovo")}
            onClick={() => void osvezi()}
          >
            <Ic.refresh width={14} height={14} />
          </button>
        </span>
      </header>

      {ucitava ? (
        <div style={{ ...tiho, padding: "6px 0" }}>{t("common.loading", "Učitavanje…")}</div>
      ) : !podaci?.available ? (
        <div style={tiho}>{razlog(podaci, t)}</div>
      ) : (
        <>
          <div style={gornjiRed}>
            <Ocena
              vrednost={podaci.creditScore}
              trend={podaci.creditScoreTrend}
              prosek={podaci.industryAvgScore}
              mesto={podaci.industryRank}
              ukupno={podaci.industryTotal}
              grana={podaci.industryName}
              locale={locale}
              t={t}
            />
            <div style={znakoviOkvir}>
              {podaci.alarms.length === 0 ? (
                <Znak ton="ok" tekst={t("creditCheck.clean", "Bez upozorenja")} />
              ) : (
                podaci.alarms.map((a) => (
                  <Znak
                    key={a.code}
                    ton={a.severity === "CRITICAL" ? "err" : "warn"}
                    tekst={opisAlarma(a, t)}
                    // Broj sporova bez predmeta ne govori šta da se radi; ko tuži, po kom osnovu
                    // i za koliko stoji iza istog znaka.
                    detalji={detaljiZnaka(a, podaci, locale, t)}
                  />
                ))
              )}
            </div>
          </div>

          <dl style={brojevi}>
            <Podatak
              oznaka={t("creditCheck.revenues", "Poslovni prihod")}
              vrednost={novac(podaci.revenues, podaci.financeCurrency, locale)}
              napomena={podaci.reportYear ? String(podaci.reportYear) : undefined}
            />
            <Podatak
              oznaka={t("creditCheck.netResult", "Neto rezultat")}
              vrednost={novac(podaci.netResult, podaci.financeCurrency, locale)}
              tonVrednosti={podaci.netResult != null && podaci.netResult < 0 ? "var(--err)" : undefined}
            />
            <Podatak
              oznaka={t("creditCheck.employees", "Zaposlenih")}
              vrednost={podaci.employees != null ? formatNumber(podaci.employees, locale) : "—"}
            />
          </dl>
        </>
      )}
    </section>
  );
}

/**
 * Ocena od 0 do 100, sa trakom koja je boji.
 *
 * Traka nosi boju praga, ne ukrasa: ispod 40 je crveno, do 70 žuto, iznad zeleno. Broj bez te
 * skale ne govori ništa nikome ko ne radi sa bonitetom svaki dan — „71" je dobra vest, a izgleda
 * kao loša ocena u školi.
 */
/** Ispod 40 crveno, do 70 žuto, iznad zeleno; ista skala u obe varijante kartice. */
function bojaOcene(v: number | null): string {
  if (v == null) return "var(--ink-3)";
  return v < 40 ? "var(--err)" : v < 70 ? "var(--warn)" : "var(--ok)";
}

function Ocena({
  vrednost, trend, prosek, mesto, ukupno, grana, locale, t,
}: {
  vrednost: number | null;
  trend: number | null;
  prosek: number | null;
  mesto: number | null;
  ukupno: number | null;
  grana: string | null;
  locale: string;
  t: (k: string, d: string, o?: Record<string, unknown>) => string;
}) {
  if (vrednost == null) return null;
  const boja = bojaOcene(vrednost);
  // Registar šalje 1 za rast i 2 za pad; 0 i prazno znače „isto".
  const strelica = trend === 1 ? "▲" : trend === 2 ? "▼" : null;
  return (
    <div style={ocenaOkvir}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <span style={{ ...ocenaBroj, color: boja }}>{vrednost}</span>
        {strelica && (
          <span style={{ fontSize: 12, color: trend === 1 ? "var(--ok)" : "var(--err)" }}>{strelica}</span>
        )}
      </div>
      {/* Skala je 0–100, ali nije ni ocena u školi ni procenat. Oznaka praga na traci kaže gde je
          prosek delatnosti, pa se broj čita kao sud a ne kao gola cifra. */}
      <div style={traka} aria-hidden="true" title={grana ?? undefined}>
        <div style={{ width: `${Math.max(0, Math.min(100, vrednost))}%`, height: "100%", background: boja, borderRadius: 999 }} />
        {prosek != null && (
          <span style={{ ...oznakaProseka, left: `${Math.max(0, Math.min(100, prosek))}%` }} />
        )}
      </div>
      <span style={tiho}>{t("creditCheck.score", "Kreditna ocena")}</span>
      {prosek != null && (
        <span style={tiho}>
          {t("creditCheck.industryAvg", "prosek grane {{n}}",
             { n: formatNumber(prosek, locale, { maximumFractionDigits: 0 }) })}
        </span>
      )}
      {mesto != null && ukupno != null && (
        <span style={tiho} title={grana ?? undefined}>
          {t("creditCheck.industryRank", "{{mesto}}. od {{ukupno}}",
             { mesto: formatNumber(mesto, locale, { maximumFractionDigits: 0 }),
               ukupno: formatNumber(ukupno, locale, { maximumFractionDigits: 0 }) })}
        </span>
      )}
    </div>
  );
}

/**
 * Aktivni sporovi, iza znaka.
 *
 * Tužen je onaj koji odgovara — to je obaveza koja tek dolazi, pa ide prvi i crveno. Spor u kom
 * je kupac tužilac nije njegov rizik i stoji niže, radi potpunosti.
 */
/**
 * Šta stoji iza znaka o ranijim blokadama.
 *
 * Broj blokada sam po sebi ne kaže koliko je bilo teško: dve blokade od po dan nisu isto što i
 * dve od po četrdeset dana. Zato uz broj ide i ukupno trajanje, a prva rečenica kaže ono zbog
 * čega se ovaj znak i gleda — da danas blokade nema.
 */
function Blokade({
  status, broj, dana, locale, t,
}: {
  status: string | null;
  broj: number | null;
  dana: number | null;
  locale: string;
  t: (k: string, d: string, o?: Record<string, unknown>) => string;
}) {
  return (
    <>
      <span style={sporRed}>
        <b style={{ color: "var(--ok)" }}>
          {t("creditCheck.blockage.clearNow", "Račun danas nije u blokadi")}
        </b>
        {status && <span style={{ color: "var(--ink-4)" }}>{status}</span>}
      </span>
      <span style={sporRed}>
        <span style={{ color: "var(--ink-3)" }}>
          {t("creditCheck.blockage.everCount", "Ranije blokada: {{n}}",
             { n: formatNumber(broj ?? 0, locale) })}
        </span>
        {dana != null && dana > 0 && (
          <span style={{ color: "var(--ink-3)" }}>
            {t("creditCheck.blockage.everDays", "Ukupno dana u blokadi: {{n}}",
               { n: formatNumber(dana, locale) })}
          </span>
        )}
      </span>
    </>
  );
}

function Sporovi({
  sporovi, locale, t,
}: {
  sporovi: CreditDispute[];
  locale: string;
  t: (k: string, d: string, o?: Record<string, unknown>) => string;
}) {
  if (sporovi.length === 0) return null;
  const poredjani = [...sporovi].sort((a, b) =>
    (a.role === "TUŽENI" ? 0 : 1) - (b.role === "TUŽENI" ? 0 : 1));
  return (
    <>
      {poredjani.map((s, i) => {
        const tuzen = s.role === "TUŽENI";
        return (
          <span key={s.caseNumber ?? i} style={sporRed}>
            <span style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
              <b style={{ color: tuzen ? "var(--err)" : "var(--ink-2)" }}>
                {tuzen
                  ? t("creditCheck.dispute.defendant", "Tužena strana")
                  : t("creditCheck.dispute.plaintiff", "Tužilac")}
              </b>
              {s.amount != null && s.amount > 0 && (
                <span style={{ fontVariantNumeric: "tabular-nums" }}>
                  {formatMoney(s.amount, s.currency ?? "RSD", locale, { maximumFractionDigits: 0 })}
                </span>
              )}
            </span>
            <span style={{ color: "var(--ink-3)" }}>
              {[s.caseNumber, s.basis].filter(Boolean).join(" · ")}
            </span>
            <span style={{ color: "var(--ink-4)" }}>
              {[s.courtName, s.receivedOn ? formatDate(s.receivedOn) : null].filter(Boolean).join(" · ")}
            </span>
          </span>
        );
      })}
    </>
  );
}

/**
 * Jedno upozorenje. Kad iza njega stoje predmeti, vide se na prelazak mišem.
 *
 * Oblačić se otvara i na fokus, ne samo na miša: znak je dugme kad ima šta da pokaže, pa se do
 * njega stiže i tastaturom.
 */
function Znak({
  ton, tekst, detalji,
}: { ton: "ok" | "warn" | "err"; tekst: string; detalji?: React.ReactNode }) {
  const boja = ton === "ok" ? "var(--ok)" : ton === "warn" ? "var(--warn)" : "var(--err)";
  const [otvoren, setOtvoren] = useState(false);
  const stil: CSSProperties = {
    ...znak, color: boja, borderColor: boja,
    background: `color-mix(in srgb, ${boja} 8%, transparent)`,
  };

  if (!detalji) {
    return (
      <span style={stil}>
        <span style={{ ...tacka, background: boja }} />
        {tekst}
      </span>
    );
  }

  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      <button
        type="button"
        style={{ ...stil, cursor: "help" }}
        aria-expanded={otvoren}
        onMouseEnter={() => setOtvoren(true)}
        onMouseLeave={() => setOtvoren(false)}
        onFocus={() => setOtvoren(true)}
        onBlur={() => setOtvoren(false)}
        onClick={() => setOtvoren((x) => !x)}
      >
        <span style={{ ...tacka, background: boja }} />
        {tekst}
      </button>
      {otvoren && <span role="tooltip" style={oblacic}>{detalji}</span>}
    </span>
  );
}

function Podatak({
  oznaka, vrednost, napomena, tonVrednosti,
}: { oznaka: string; vrednost: string; napomena?: string; tonVrednosti?: string }) {
  return (
    <div>
      <dt style={tiho}>{oznaka}{napomena ? ` ${napomena}` : ""}</dt>
      <dd style={{ ...podatakVrednost, color: tonVrednosti ?? "var(--ink)" }}>{vrednost}</dd>
    </div>
  );
}

/** Koji znak nosi objašnjenje ispod sebe, i koje. */
function detaljiZnaka(
  a: CreditAlarm,
  p: CreditCheck,
  locale: string,
  t: (k: string, d: string, o?: Record<string, unknown>) => string,
): React.ReactNode {
  if (a.code === "disputes") return <Sporovi sporovi={p.disputes} locale={locale} t={t} />;
  if (a.code === "blockageHistory") {
    return <Blokade status={p.accountStatus} broj={p.blockageCountEver}
                    dana={p.blockageDaysTotal} locale={locale} t={t} />;
  }
  return undefined;
}

function opisAlarma(a: CreditAlarm, t: (k: string, d: string, o?: Record<string, unknown>) => string): string {
  switch (a.code) {
    case "companyStatus":
      return a.value ?? t("creditCheck.alarm.status", "Firma nije aktivna");
    case "accountBlocked":
      return /^\d+$/.test(a.value ?? "")
        ? t("creditCheck.alarm.blockedDays", "Račun u blokadi {{n}} dana", { n: a.value })
        : t("creditCheck.alarm.blocked", "Račun u blokadi");
    case "disputes": {
      // Srpski traži tri oblika: 1 spor, 2–4 spora, 5+ sporova. i18next to zna preko `count`.
      const n = Number(a.value ?? 0);
      return t("creditCheck.alarm.disputes", "{{count}} aktivnih sporova", { count: n });
    }
    case "executions":
      return t("creditCheck.alarm.executions", "{{count}} izvršenja", { count: Number(a.value ?? 0) });
    case "lowScore":
      return t("creditCheck.alarm.lowScore", "Niska ocena ({{n}})", { n: a.value });
    case "blockageHistory":
      // „Ranije blokade: 2" ostavlja otvoreno pitanje da li je firma u blokadi SADA, a to je
      // jedino što se sa ovog ekrana odlučuje. Zato znak počinje odgovorom na to pitanje.
      return t("creditCheck.alarm.blockageHistory", "Nije u blokadi, ranije {{count}} puta",
               { count: Number(a.value ?? 0) });
  }
}

function razlog(
  p: CreditCheck | null,
  t: (k: string, d: string, o?: Record<string, unknown>) => string,
): string {
  switch (p?.reason) {
    case "FOREIGN":
      return t("creditCheck.none.foreign", "Registar pokriva Srbiju; strana firma se ne proverava.");
    case "NO_TAX_NUMBER":
      return t("creditCheck.none.noTaxNumber", "Firma nema upisan PIB, pa nema po čemu da se nađe.");
    case "NOT_FOUND":
      return t("creditCheck.none.notFound", "Registar ne poznaje firmu sa ovim PIB-om.");
    case "FAILED":
      return t("creditCheck.none.failed", "Registar nije odgovorio pri poslednjem pokušaju.");
    default:
      return t("creditCheck.none.unknown", "Provera nije dostupna.");
  }
}

const novac = (n: number | null, valuta: string | null, locale: string) =>
  n == null ? "—" : formatMoney(n, valuta ?? "EUR", locale, { maximumFractionDigits: 0 });

const okvir: CSSProperties = {
  background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-md, 12px)",
  padding: 14, display: "flex", flexDirection: "column", gap: 10,
  minWidth: 320, maxWidth: 460, flex: "1 1 340px",
};
/** Ista geometrija kao `.kpi`, da red pokazatelja ostane ujednačen. */
const zbijeno: CSSProperties = {
  background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-md, 12px)",
  padding: 18, minHeight: 112, display: "flex", flexDirection: "column", gap: 8,
};
const zaglavlje: CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
};
const naslov: CSSProperties = {
  fontSize: 11.5, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", color: "var(--ink-3)",
};
const desno: CSSProperties = { display: "flex", alignItems: "center", gap: 4 };
const tiho: CSSProperties = { fontSize: 12, color: "var(--ink-3)" };
const dugmeOsvezi: CSSProperties = {
  width: 24, height: 24, minWidth: 24, padding: 0, borderRadius: 6,
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  color: "var(--ink-4)", flex: "none",
};
const gornjiRed: CSSProperties = { display: "flex", alignItems: "flex-start", gap: 16 };
const ocenaOkvir: CSSProperties = {
  display: "flex", flexDirection: "column", gap: 4, flex: "none", width: 96,
};
const ocenaBroj: CSSProperties = {
  fontSize: 30, fontWeight: 600, lineHeight: 1, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums",
};
const traka: CSSProperties = {
  position: "relative", height: 5, borderRadius: 999, background: "var(--surface-muted)",
};
const znakoviOkvir: CSSProperties = {
  display: "flex", flexWrap: "wrap", gap: 6, alignContent: "flex-start", flex: 1, paddingTop: 2,
};
const znak: CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
  padding: "3px 9px", borderRadius: 999, border: "1px solid", fontSize: 12, fontWeight: 500,
};
const tacka: CSSProperties = { width: 6, height: 6, borderRadius: "50%", flex: "none" };
/** Gde je prosek delatnosti na traci; bez toga je 88 samo broj. */
const oznakaProseka: CSSProperties = {
  position: "absolute", top: -2, width: 2, height: 9, background: "var(--ink-3)",
  borderRadius: 1, transform: "translateX(-1px)",
};
const oblacic: CSSProperties = {
  position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 40, width: 280,
  display: "flex", flexDirection: "column", gap: 8,
  background: "var(--surface)", border: "1px solid var(--border)",
  borderRadius: "var(--r-md, 12px)", boxShadow: "var(--shadow-2, var(--shadow-1))",
  padding: "10px 12px", fontSize: 12, fontWeight: 400, color: "var(--ink)",
  whiteSpace: "normal", textAlign: "left", cursor: "default",
};
const sporRed: CSSProperties = {
  display: "flex", flexDirection: "column", gap: 2, lineHeight: 1.4,
};
const brojevi: CSSProperties = {
  display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, margin: 0,
  paddingTop: 10, borderTop: "1px solid var(--border)",
};
const podatakVrednost: CSSProperties = {
  margin: "2px 0 0", fontSize: 14, fontWeight: 600, fontVariantNumeric: "tabular-nums",
};
